import _ from 'lodash'
import { i18n } from '../../../i18n'
import {
  averageIfs,
  hourCutSerie,
  hoursOfTheDay,
  roundSerieToEvenNumbers,
  rangeValues,
  tempCutSerie,
  dayHours,
  yearMonths,
  monthsSumSerie,
} from './calibration-util'
import { BuildingEnergyModelFragment } from '../../../generated/graphql'
import { powerPerAreaUnit } from '../../../util/formatters'
import {
  createChartSerieValues,
  createDefaultLineChartSerie,
  CustomLineStyles,
  CustomLineType,
  CustomSeries,
} from './chart-util'
import { hoursInYear } from '../../../util/hours-in-year'
import { hoursInWeek } from '../../../util/hours-in-week'

const DEFAULT_LINETYPE = CustomLineStyles.Dotted
const DEFAULT_LINEWIDTH = 4

const measuredDataByHour = (
  measuredSerie: number[],
  cutSerie: number[],
): (number | null)[] => {
  const hoursOfTheDaySerie = hoursOfTheDay(0)

  const values = Array<number | null>(24).fill(0)

  for (let i = 0; i < values.length; i++) {
    values[i] = averageIfs(
      measuredSerie,
      [
        {
          range: hoursOfTheDaySerie,
          rangeCriteria: i,
          criteriaFunc: (item, value) => item === value,
        },
        {
          range: cutSerie,
          rangeCriteria: 1,
          criteriaFunc: (item, value) => item === value,
        },
      ],
      1,
    )
  }

  return values
}

const measuredDataByEvenTempRange = (
  evenTempValues: number[],
  measuredSerie: number[],
  outsideTempHourSerie: number[],
  cutSerie: number[],
  allowNullValues = false,
): (number | null)[] => {
  const evenTempSerie = roundSerieToEvenNumbers(outsideTempHourSerie)

  const values = Array<number | null>(evenTempValues.length).fill(0)

  for (let i = 0; i < evenTempValues.length; i++) {
    values[i] = averageIfs(
      measuredSerie,
      [
        {
          range: evenTempSerie,
          rangeCriteria: evenTempValues[i],
          criteriaFunc: (item, value) => item === value,
        },
        {
          range: cutSerie,
          rangeCriteria: 1,
          criteriaFunc: (item, value) => item === value,
        },
      ],
      1,
      allowNullValues,
    )
  }

  return values
}

export const calcMeasuredHeatingDataByHour = (
  model: BuildingEnergyModelFragment,
  cutSerie: number[],
): CustomSeries => {
  const id = `${i18n.t(
    'buildingEnergyModels.calibration.dataTypes.measuredHeating',
  )} ${powerPerAreaUnit}`

  const hourValues = dayHours()

  const result = createDefaultLineChartSerie(
    id,
    hourValues,
    DEFAULT_LINETYPE,
    DEFAULT_LINEWIDTH,
  )

  if (!model.calibrationArea) return result

  const area = model.calibrationArea
  const correction = model.calibrationCorrection ?? 1

  const measuredDensitySerie = model.calHeatingHourSeries?.values.map(
    (v) => _.round(v / area, 5) * correction,
  )

  if (!measuredDensitySerie) return result

  const values = measuredDataByHour(measuredDensitySerie, cutSerie)

  result.data = createChartSerieValues(hourValues, values)

  return result
}

const calcMeasuredDensityByEvenTempRange = (
  id: string,
  serie: number[] | undefined,
  model: BuildingEnergyModelFragment,
  tempRangeStart: number,
  tempRangeEnd: number,
  tempRangeStep: number,
  cutSerie: number[],
  allowNullValues = false,
  lineType?: CustomLineType,
  lineWidth?: number,
): CustomSeries => {
  const temperatureValues = rangeValues(
    tempRangeStart,
    tempRangeEnd,
    tempRangeStep,
  )

  const result = createDefaultLineChartSerie(
    id,
    temperatureValues,
    lineType ?? DEFAULT_LINETYPE,
    lineWidth ?? DEFAULT_LINEWIDTH,
  )

  if (!model.calibrationArea) return result

  const area = model.calibrationArea
  const correction = model.calibrationCorrection ?? 1

  if (!model.calOutdoorHourSeries?.values) return result

  const outsideTempHourSerie = model.calOutdoorHourSeries.values

  if (!serie) return result

  const measuredDensitySerie = serie.map(
    (v) => _.round(v / area, 5) * correction,
  )

  if (!measuredDensitySerie) return result

  const values = measuredDataByEvenTempRange(
    temperatureValues,
    measuredDensitySerie,
    outsideTempHourSerie,
    cutSerie,
    allowNullValues,
  )

  result.data = createChartSerieValues(temperatureValues, values)

  return result
}

const calcMeasuredDensitySerieOfRangeByHour = (
  id: string,
  rangeStart: number,
  rangeEnd: number,
  serie: number[] | undefined,
  area: number | undefined | null,
  correctionFactor: number | undefined | null,
  sortDescending?: boolean,
  lineType = CustomLineStyles.Solid,
  lineWidth = 2,
): CustomSeries => {
  const hours = Array<number>(rangeEnd - rangeStart)
    .fill(0)
    .map((i, index) => index)

  const result = createDefaultLineChartSerie(id, hours, lineType, lineWidth)

  if (!serie) return result
  if (!area) return result

  const correction = correctionFactor ?? 1

  let densitySerie = serie
    .slice(rangeStart, rangeEnd)
    .map((v) => _.round(v / area, 3) * correction)

  if (!densitySerie) return result

  if (sortDescending) densitySerie = densitySerie.sort((a, b) => b - a)

  result.data = createChartSerieValues(hours, densitySerie)

  return result
}

export const calcMeasuredHeatingDataInYearByHour = (
  model: BuildingEnergyModelFragment,
): CustomSeries =>
  calcMeasuredDensitySerieOfRangeByHour(
    `${i18n.t(
      'buildingEnergyModels.calibration.dataTypes.measuredHeating',
    )} ${powerPerAreaUnit}`,
    0,
    hoursInYear,
    model.calHeatingHourSeries?.values,
    model.calibrationArea,
    model.calibrationCorrection,
  )

export const calcMeasuredHeatingDataInFirstWeekByHour = (
  model: BuildingEnergyModelFragment,
): CustomSeries =>
  calcMeasuredDensitySerieOfRangeByHour(
    `${i18n.t(
      'buildingEnergyModels.calibration.dataTypes.measuredHeating',
    )} ${powerPerAreaUnit}`,
    0,
    hoursInWeek,
    model.calHeatingHourSeries?.values,
    model.calibrationArea,
    model.calibrationCorrection,
    undefined,
    CustomLineStyles.Dotted,
    4,
  )

export const calcSortedMeasuredHeatingDataInYearByHour = (
  model: BuildingEnergyModelFragment,
): CustomSeries =>
  calcMeasuredDensitySerieOfRangeByHour(
    `${i18n.t(
      'buildingEnergyModels.calibration.dataTypes.measuredHeating',
    )} ${powerPerAreaUnit}`,
    0,
    hoursInYear,
    model.calHeatingHourSeries?.values,
    model.calibrationArea,
    model.calibrationCorrection,
    true,
    CustomLineStyles.Dotted,
    4,
  )

const calcSumMeasuredDensitySerieInYearByMonth = (
  id: string,
  year: number,
  serie: number[] | undefined,
  area: number | undefined | null,
  correctionFactor: number | undefined | null,
): CustomSeries => {
  const monthValues = yearMonths()

  const result = createDefaultLineChartSerie(
    id,
    monthValues,
    DEFAULT_LINETYPE,
    DEFAULT_LINEWIDTH,
  )

  if (!serie) return result
  if (!area) return result

  const correction = correctionFactor ?? 1

  const densitySerie = serie.map((v) => _.round(v / area, 5) * correction)

  if (!densitySerie) return result

  const measuredData = monthsSumSerie(densitySerie, year)

  result.data = createChartSerieValues(monthValues, measuredData)

  return result
}

export const calcSumMeasuredHeatingDataInYearByMonth = (
  model: BuildingEnergyModelFragment,
  year: number,
): CustomSeries =>
  calcSumMeasuredDensitySerieInYearByMonth(
    i18n.t('buildingEnergyModels.calibration.dataTypes.measuredHeating'),
    year,
    model.calHeatingHourSeries?.values,
    model.calibrationArea,
    model.calibrationCorrection,
  )

export const calcDhwMeasuredDataInSummerByHour = (
  model: BuildingEnergyModelFragment,
  calScheduleSerie: number[],
): CustomSeries => {
  const summerCutSerie = tempCutSerie(
    model.calOutdoorHourSeries?.values ?? [],
    calScheduleSerie,
    model.calibrationLimitValues?.summerTempStart ?? 100,
    model.calibrationLimitValues?.summerTempEnd ?? -100,
  )

  return calcMeasuredHeatingDataByHour(model, summerCutSerie)
}

export const calcDhwMeasuredDataInZeroTempByHour = (
  model: BuildingEnergyModelFragment,
  calScheduleSerie: number[],
): CustomSeries => {
  const cutSerie = tempCutSerie(
    model.calOutdoorHourSeries?.values ?? [],
    calScheduleSerie,
    model.calibrationLimitValues?.zeroWeatherTempStart ?? 100,
    model.calibrationLimitValues?.zeroWeatherTempEnd ?? -100,
  )

  return calcMeasuredHeatingDataByHour(model, cutSerie)
}

export const calcDhwMeasuredDataInWinterTempByHour = (
  model: BuildingEnergyModelFragment,
  calScheduleSerie: number[],
): CustomSeries => {
  const cutSerie = tempCutSerie(
    model.calOutdoorHourSeries?.values ?? [],
    calScheduleSerie,
    model.calibrationLimitValues?.winterTempStart ?? 100,
    model.calibrationLimitValues?.winterTempEnd ?? -100,
  )

  return calcMeasuredHeatingDataByHour(model, cutSerie)
}

export const calcMeasuredHeatingInNightByEvenTempRange = (
  model: BuildingEnergyModelFragment,
  calScheduleSerie: number[],
  tempRangeStart: number,
  tempRangeEnd: number,
  tempRangeStep: number,
): CustomSeries => {
  const cutSerie = hourCutSerie(
    calScheduleSerie,
    model.calibrationLimitValues?.nightStartHour ?? -1,
    model.calibrationLimitValues?.nightEndHour ?? -1,
  )

  return calcMeasuredDensityByEvenTempRange(
    `${i18n.t(
      'buildingEnergyModels.calibration.dataTypes.measuredHeating',
    )} ${powerPerAreaUnit}`,
    model.calHeatingHourSeries?.values,
    model,
    tempRangeStart,
    tempRangeEnd,
    tempRangeStep,
    cutSerie,
  )
}

export const calcMeasuredHeatingInDayByEvenTempRange = (
  model: BuildingEnergyModelFragment,
  calScheduleSerie: number[],
  tempRangeStart: number,
  tempRangeEnd: number,
  tempRangeStep: number,
): CustomSeries => {
  const cutSerie = hourCutSerie(
    calScheduleSerie,
    model.calibrationLimitValues?.dayStartHour ?? -1,
    model.calibrationLimitValues?.dayEndHour ?? -1,
  )

  return calcMeasuredDensityByEvenTempRange(
    `${i18n.t(
      'buildingEnergyModels.calibration.dataTypes.measuredHeating',
    )} ${powerPerAreaUnit}`,
    model.calHeatingHourSeries?.values,
    model,
    tempRangeStart,
    tempRangeEnd,
    tempRangeStep,
    cutSerie,
  )
}

export const calcMeasuredCoolingDensity = (
  model: BuildingEnergyModelFragment,
): CustomSeries => {
  const id = `${i18n.t(
    'buildingEnergyModels.calibration.dataTypes.measuredCooling',
  )} ${powerPerAreaUnit}`

  const result: CustomSeries = { id, data: [] }

  const coolingSerie = model.calCoolingHourSeries?.values
  if (!coolingSerie) return result

  if (!model.calOutdoorHourSeries?.values) return result

  const outsideTempHourSerie = model.calOutdoorHourSeries.values

  if (!model.calibrationArea) return result

  const area = model.calibrationArea
  const correction = model.calibrationCorrection ?? 1

  const measuredDensitySerie = coolingSerie.map(
    (v) => _.round(v / area, 5) * correction,
  )

  if (!measuredDensitySerie) return result

  result.data = createChartSerieValues(
    outsideTempHourSerie,
    measuredDensitySerie,
  )

  return result
}

export const calcSortedMeasuredCoolingDensityInYearByHour = (
  model: BuildingEnergyModelFragment,
): CustomSeries =>
  calcMeasuredDensitySerieOfRangeByHour(
    i18n.t('buildingEnergyModels.calibration.dataTypes.measuredCooling'),
    0,
    hoursInYear,
    model.calCoolingHourSeries?.values,
    model.calibrationArea,
    model.calibrationCorrection,
    true,
    CustomLineStyles.Solid,
    4,
  )

export const calcSumMeasuredCoolingDataInYearByMonth = (
  model: BuildingEnergyModelFragment,
  year: number,
): CustomSeries =>
  calcSumMeasuredDensitySerieInYearByMonth(
    i18n.t('buildingEnergyModels.calibration.dataTypes.measuredHeating'),
    year,
    model.calCoolingHourSeries?.values,
    model.calibrationArea,
    model.calibrationCorrection,
  )

export const calcSortedMeasuredElectricityDensityInYearByHour = (
  model: BuildingEnergyModelFragment,
): CustomSeries =>
  calcMeasuredDensitySerieOfRangeByHour(
    i18n.t('buildingEnergyModels.calibration.dataTypes.measuredNetElectricity'),
    0,
    hoursInYear,
    model.calElectricityHourSeries?.values,
    model.calibrationArea,
    model.calibrationCorrection,
    true,
    CustomLineStyles.Solid,
    4,
  )

export const calcMeasuredElectricityDataByHour = (
  model: BuildingEnergyModelFragment,
  calScheduleSerie: number[],
): CustomSeries => {
  const id = `${i18n.t(
    'buildingEnergyModels.calibration.dataTypes.measuredElectricity',
  )} ${powerPerAreaUnit}`

  const hourValues = dayHours()

  const result = createDefaultLineChartSerie(
    id,
    hourValues,
    DEFAULT_LINETYPE,
    DEFAULT_LINEWIDTH,
  )

  if (!model.calibrationArea) return result

  const area = model.calibrationArea
  const correction = model.calibrationCorrection ?? 1

  const measuredDensitySerie = model.calElectricityHourSeries?.values.map(
    (v) => _.round(v / area, 5) * correction,
  )

  if (!measuredDensitySerie) return result

  const values = measuredDataByHour(measuredDensitySerie, calScheduleSerie)

  result.data = createChartSerieValues(hourValues, values)

  return result
}

export const calcSumMeasuredElectricityDataInYearByMonth = (
  model: BuildingEnergyModelFragment,
  year: number,
): CustomSeries =>
  calcSumMeasuredDensitySerieInYearByMonth(
    i18n.t('buildingEnergyModels.calibration.dataTypes.measuredNetElectricity'),
    year,
    model.calElectricityHourSeries?.values,
    model.calibrationArea,
    model.calibrationCorrection,
  )

export const calcMeasuredElectricityInDayByEvenTempRange = (
  model: BuildingEnergyModelFragment,
  calScheduleSerie: number[],
  tempRangeStart: number,
  tempRangeEnd: number,
  tempRangeStep: number,
): CustomSeries => {
  const cutSerie = hourCutSerie(
    calScheduleSerie,
    model.calibrationLimitValues?.dayStartHour ?? -1,
    model.calibrationLimitValues?.dayEndHour ?? -1,
  )

  return calcMeasuredDensityByEvenTempRange(
    i18n.t(
      'buildingEnergyModels.calibration.dataTypes.measuredAverageElectricityPower',
    ),
    model.calElectricityHourSeries?.values,
    model,
    tempRangeStart,
    tempRangeEnd,
    tempRangeStep,
    cutSerie,
    true,
    CustomLineStyles.Solid,
  )
}
